import { defineMessages } from 'react-intl';

import { FIELD_NAMES } from './constants';

const { CITY, COMPANY, COUNTRY_CODE, EMAIL, LINE1, LINE2, LINE3, NAME, PHONE, POSTAL_CODE, STATE } = FIELD_NAMES;

export const commonPlaceholders = defineMessages({
  [LINE2]: {
    description: 'common placeholder for line 2',
    defaultMessage: 'Address, continued',
    id: 'Vb7NXO',
  },
  [LINE3]: {
    description: 'common placeholder for line 3',
    defaultMessage: 'Address, continued',
    id: 'sQ0bPS',
  },
});

export const placeholdersByCountry = {
  US: defineMessages({
    [LINE2]: {
      description: 'address line 2 placeholder for US',
      defaultMessage: 'Street address, continued',
      id: 'y0ZpGj',
    },
    [PHONE]: {
      description: 'telephone input placeholder for US',
      defaultMessage: '(555) 555-5555',
      id: 'qhwoBP',
    },
  }),
  GB: defineMessages({
    [LINE2]: {
      description: 'address line 2 placeholder for GB',
      defaultMessage: 'Address, continued',
      id: 'FjtSFO',
    },
  }),
  AU: defineMessages({
    [LINE2]: {
      description: 'line 2 placeholder for AU',
      defaultMessage: 'Apartment, suite, unit, building, floor, etc.',
      id: 'F7+iWR',
    },
  }),
  DE: defineMessages({
    [LINE1]: {
      description: 'line 1 placeholder for DE',
      defaultMessage: 'Street and house number',
      id: '/wmcbz',
    },
  }),
};

export const messagesByCountry = {
  US: defineMessages({
    [LINE1]: {
      description: 'address line 1 label for US',
      defaultMessage: 'Street Address',
      id: '4ixQwN',
    },
    [POSTAL_CODE]: {
      description: 'postalCode label for US',
      defaultMessage: 'ZIP Code',
      id: 'lzUeMe',
    },
  }),
  CA: defineMessages({
    [STATE]: {
      description: 'state label for CA',
      defaultMessage: 'Province',
      id: 'rr1NCJ',
    },
  }),
  GB: defineMessages({
    [LINE1]: {
      description: 'address line 1 label for GB',
      defaultMessage: 'Address',
      id: 'HTraMY',
    },
    [STATE]: {
      description: 'state field label for GB, GB does not have states, we save their "county" as a state',
      defaultMessage: 'County',
      id: 'hpPbuR',
    },
    [CITY]: {
      description: 'city field label for GB',
      defaultMessage: 'Town/City',
      id: 'C80Yim',
    },
  }),
  AU: defineMessages({
    [CITY]: {
      description: 'city field label for AU',
      defaultMessage: 'Suburb',
      id: 'bX7lUj',
    },
    [STATE]: {
      description: 'state field label for AU',
      defaultMessage: 'State',
      id: 'urVJc+',
    },
    [POSTAL_CODE]: {
      description: 'postalCode label for AU',
      defaultMessage: 'Postcode',
      id: 'O0i2JZ',
    },
  }),
  NZ: defineMessages({
    [STATE]: {
      description: 'state field label for NZ',
      defaultMessage: 'Town/City',
      id: 'rJTeog',
    },
    [CITY]: {
      description: 'city field label for NZ',
      defaultMessage: 'Suburb',
      id: 'yso8Fi',
    },
    [POSTAL_CODE]: {
      description: 'postal code field label for NZ',
      defaultMessage: 'Postcode',
      id: 'DPc/Zz',
    },
  }),
  FR: defineMessages({
    [STATE]: {
      description: 'state label for FR',
      defaultMessage: 'Province',
      id: 'DRs1d9',
    },
  }),
};

export const subLabelMessages = defineMessages({
  phoneNeededForSomeCarriers: {
    description: 'common sublabel telling user why we require phone field',
    defaultMessage: 'Many carriers require a phone number for shipments.',
    id: 'VGdUa5',
  },
  multiLineEmailSubLabel: {
    description: 'email sublabel telling user how to enter multiple emails',
    defaultMessage: 'For multiple emails, separate with a comma. Ex: example1@email.com, example2@email.com',
    id: 'xApm62',
  },
});

export const commonMessages = defineMessages({
  [NAME]: {
    description: 'common label for name field including; US and GB',
    defaultMessage: 'Full Name',
    id: 'pio2+1',
  },
  [CITY]: {
    description: 'common label for city field',
    defaultMessage: 'City',
    id: 'jC1Gf9',
  },
  [COMPANY]: {
    description: 'common label for company field',
    defaultMessage: 'Company',
    id: '/iZrNi',
  },
  [COUNTRY_CODE]: {
    description: 'common label for country field',
    defaultMessage: 'Country',
    id: '9UZkcM',
  },
  [STATE]: {
    description: 'common label for state field',
    defaultMessage: 'State',
    id: 'KzbKoL',
  },
  [LINE1]: {
    description: 'common label for address line 1 field',
    defaultMessage: 'Address',
    id: 'gA/A/4',
  },
  [POSTAL_CODE]: {
    description: 'address.postalCode',
    defaultMessage: 'Postal Code',
    id: 'yTpyx1',
  },
  [PHONE]: {
    description: 'common label for phone field',
    defaultMessage: 'Phone',
    id: 'DE38qf',
  },
  [EMAIL]: {
    description: 'common label for email field',
    defaultMessage: 'Email',
    id: 'SDOdqg',
  },
  fieldRequired: {
    description: 'common error message for when a required field is missing',
    defaultMessage: 'Required',
    id: 'g/iqq9',
  },
  optionalFieldSubLabel: {
    description: 'optional field sublabel',
    defaultMessage: '(Optional)',
    id: 'MpWpRw',
  },
  emailFormatValidationError: {
    description: 'Email format validation error',
    defaultMessage: 'Must be Valid Email',
    id: 'y7SDsW',
  },
  recipientEmailFormatValidationError: {
    description: 'Email format validation error',
    defaultMessage: '{firstInvalidEmail} is not a valid email address',
    id: '0xKaLu',
  },
  companyLengthLimit: {
    description: 'length limit message for the company field',
    defaultMessage: 'Company name is limited to 50 characters',
    id: '6QJcnl',
  },
  emailLengthLimit: {
    description: 'length limit message for the email field',
    defaultMessage: 'Cannot exceed 100 characters',
    id: 'lqiAhK',
  },
});

export const validationTooltipsByCountry = {
  AU: defineMessages({
    [STATE]: {
      description: 'tooltip state field error for AU',
      defaultMessage: 'State is required.',
      id: 'Jz9+I/',
    },
    [POSTAL_CODE]: {
      description: 'tooltip postalCode error for AU',
      defaultMessage: 'Postcode is required.',
      id: 'DOVC7W',
    },
  }),
  CA: defineMessages({
    [STATE]: {
      description: 'tooltip state error for CA',
      defaultMessage: 'Province is required.',
      id: '/4JZvQ',
    },
    [POSTAL_CODE]: {
      description: 'tooltip postalCode error for CA',
      defaultMessage: 'Postal Code is required.',
      id: 'clcwlT',
    },
  }),
  DE: defineMessages({
    [POSTAL_CODE]: {
      description: 'tooltip required message for postal code',
      defaultMessage: 'Postal Code is required.',
      id: 'fDMVuR',
    },
  }),
  NZ: defineMessages({
    [POSTAL_CODE]: {
      description: 'tooltip required message for postcode NZ',
      defaultMessage: 'Postcode is required.',
      id: 'DmF6sl',
    },
  }),
  US: defineMessages({
    [POSTAL_CODE]: {
      description: 'tooltip postalCode error for US',
      defaultMessage: 'ZIP code is required.',
      id: 'swTvLp',
    },
    [STATE]: {
      description: 'tooltip state error for US',
      defaultMessage: 'State is required.',
      id: '0eaZj+',
    },
  }),
};

export const requiredValidationErrorsByCountry = {
  AU: defineMessages({
    [CITY]: {
      description: 'city field error for AU',
      defaultMessage: 'Suburb is required.',
      id: 'bSwQoP',
    },
    [STATE]: {
      description: 'state field error for AU',
      defaultMessage: 'Req.',
      id: 'uCkUmG',
    },
    [POSTAL_CODE]: {
      description: 'postalCode error for AU',
      defaultMessage: 'Required',
      id: 'SrE2LT',
    },
  }),
  CA: defineMessages({
    [STATE]: {
      description: 'state error for CA',
      defaultMessage: 'Req.',
      id: 'fZ1rT9',
    },
    [POSTAL_CODE]: {
      description: 'required message for postal code CA',
      defaultMessage: 'Required',
      id: 'xFa04A',
    },
  }),
  DE: defineMessages({
    [POSTAL_CODE]: {
      description: 'required message for postal code DE',
      defaultMessage: 'Required',
      id: 'zNakFD',
    },
  }),
  GB: defineMessages({
    [LINE1]: {
      description: 'address line 1 error for GB',
      defaultMessage: 'Address is required.',
      id: 'riy1KU',
    },
    [STATE]: {
      description: 'state required error for GB',
      defaultMessage: 'County is required.',
      id: 'Nvck49',
    },
    [CITY]: {
      description: 'city field error for GB',
      defaultMessage: 'Town/City is required.',
      id: 'wHv1O+',
    },
  }),
  NZ: defineMessages({
    [STATE]: {
      description: 'state field error for NZ',
      defaultMessage: 'Required',
      id: 'mfFTVB',
    },
    [CITY]: {
      description: 'city field error for NZ',
      defaultMessage: 'Suburb is required.',
      id: 'aaqPUW',
    },
    [POSTAL_CODE]: {
      description: 'postal code field error for NZ',
      defaultMessage: 'Required',
      id: 'bqpUbB',
    },
  }),
  US: defineMessages({
    [LINE1]: {
      description: 'address line 1 error for US',
      defaultMessage: 'Street Address is required.',
      id: 'fgDL8E',
    },
    [POSTAL_CODE]: {
      description: 'postalCode error for US',
      defaultMessage: 'Required',
      id: 'ynEH1R',
    },
    [STATE]: {
      description: 'state error for US',
      defaultMessage: 'Req.',
      id: '/7cKVl',
    },
  }),
};

export const commonRequiredValidationErrors = defineMessages({
  [NAME]: {
    description: 'Basic error response for name field',
    defaultMessage: 'Full Name is required.',
    id: 'yxqKmh',
  },
  [CITY]: {
    description: 'Basic error response for cit field',
    defaultMessage: 'City is required.',
    id: 'UdDaL9',
  },
  [COMPANY]: {
    description: 'Basic error response for name field',
    defaultMessage: 'Company is required.',
    id: 'SVxoxu',
  },
  [COUNTRY_CODE]: {
    description: 'Basic error response for name field',
    defaultMessage: 'Country is required.',
    id: 'yCh0mr',
  },
  [STATE]: {
    description: 'Basic error response for name field',
    defaultMessage: 'State is required.',
    id: 'BCFzrN',
  },
  [LINE1]: {
    description: 'Basic error response for name field',
    defaultMessage: 'Street Address is required.',
    id: 'en5Mm0',
  },
  [POSTAL_CODE]: {
    description: 'Basic error response for name field',
    defaultMessage: 'Postal Code is required.',
    id: 'jIwiH6',
  },
  [PHONE]: {
    description: 'Basic error response for name field',
    defaultMessage: 'Phone is required.',
    id: 'KPqfV0',
  },
  [EMAIL]: {
    description: 'Basic error response for email field',
    defaultMessage: 'Email is required.',
    id: 'lOg7em',
  },
  required: {
    description: 'Basic error validation message',
    defaultMessage: 'Required',
    id: 'sB+VRY',
  },
});
